import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

import { Button } from '@/ui/base/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/base/select';

import { OPCOES_TAMANHO_PAGINA } from './tamanhoDePagina';

interface DataPaginationProps {
  /** Pagina atual, base 1. */
  page: number;
  perPage: number;
  total: number;
  onPageChange: (pagina: number) => void;
  onPerPageChange: (porPagina: number) => void;
  opcoesPorPagina?: readonly number[];
}

const OPCOES_PADRAO: readonly number[] = OPCOES_TAMANHO_PAGINA;

/**
 * Tamanho de página padrão pra quem inicia um filtro de listagem — sempre uma
 * das opções do seletor. Mora em `tamanhoDePagina.ts`, com as opções, e é
 * reexportado aqui porque os módulos já o importam deste arquivo.
 */
export { TAMANHO_PAGINA_PADRAO } from './tamanhoDePagina';

/**
 * As opções do seletor, com o tamanho atual incluído quando ele não é uma
 * delas. Um `Select` cujo valor não está na lista aparece em branco — e o
 * seletor vazio foi o defeito que a tela mostrava. A tela deve abrir numa das
 * opções (`tamanhoDePaginaValido`); isto é só a rede para a que não abre.
 */
function opcoesComAtual(opcoes: readonly number[], atual: number): number[] {
  return opcoes.includes(atual) ? [...opcoes] : [...opcoes, atual].sort((a, b) => a - b);
}

/** Páginas visíveis nos botões numerados, com reticências quando há muitas. */
function paginasVisiveis(atual: number, total: number): (number | 'reticencias')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const paginas: (number | 'reticencias')[] = [1];

  if (atual > 3) paginas.push('reticencias');

  const inicio = Math.max(2, atual - 1);
  const fim = Math.min(total - 1, atual + 1);
  for (let i = inicio; i <= fim; i++) paginas.push(i);

  if (atual < total - 2) paginas.push('reticencias');

  if (total > 1) paginas.push(total);

  return paginas;
}

/**
 * Paginacao de listagem. A paginacao e SEMPRE no servidor
 * (CLAUDE.md, "Regras de API", item 6), entao aqui so se navega entre paginas
 * que o servidor entrega — nunca se fatia uma colecao ja carregada.
 *
 * A navegacao por pagina (setas, numeros, primeira/ultima) so aparece quando
 * ha mais de uma pagina — com 1 pagina so, so o texto de contagem e o
 * seletor "Exibir" ficam, os dois alinhados a esquerda por nao terem mais
 * nada do lado direito pra empurrar contra (`justify-between` num flex de um
 * filho so).
 */
export function DataPagination({
  page,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
  opcoesPorPagina = OPCOES_PADRAO,
}: DataPaginationProps) {
  const totalPaginas = Math.max(1, Math.ceil(total / perPage));
  const paginas = paginasVisiveis(page, totalPaginas);
  const primeiraLinha = total === 0 ? 0 : (page - 1) * perPage + 1;
  const ultimaLinha = Math.min(page * perPage, total);

  return (
    <nav
      aria-label="Paginação de resultados"
      className="flex flex-col items-center justify-between gap-3 border-t px-4 py-3 sm:flex-row"
    >
      <div className="flex items-center gap-3">
        <p className="text-muted-foreground text-sm whitespace-nowrap" role="status" aria-live="polite">
          {total === 0
            ? 'Nenhum registro'
            : `${primeiraLinha}–${ultimaLinha} de ${total} registro${total !== 1 ? 's' : ''}`}
        </p>
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground text-xs whitespace-nowrap">Exibir</span>
          <Select value={String(perPage)} onValueChange={(v) => onPerPageChange(Number(v))}>
            <SelectTrigger className="h-8 w-[70px] text-xs" aria-label="Registros por página">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {opcoesComAtual(opcoesPorPagina, perPage).map((opcao) => (
                <SelectItem key={opcao} value={String(opcao)}>
                  {opcao}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {totalPaginas > 1 && (
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={page <= 1}
            onClick={() => onPageChange(1)}
            aria-label="Primeira página"
          >
            <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Página anterior"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>

          {paginas.map((p, indice) =>
            p === 'reticencias' ? (
              <span key={`reticencias-${indice}`} className="text-muted-foreground px-1 text-sm">
                …
              </span>
            ) : (
              <Button
                key={p}
                type="button"
                variant={p === page ? 'default' : 'outline'}
                size="icon"
                className="h-8 w-8 text-xs"
                onClick={() => onPageChange(p)}
                aria-label={`Página ${p}`}
                aria-current={p === page ? 'page' : undefined}
              >
                {p}
              </Button>
            ),
          )}

          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={page >= totalPaginas}
            onClick={() => onPageChange(page + 1)}
            aria-label="Próxima página"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={page >= totalPaginas}
            onClick={() => onPageChange(totalPaginas)}
            aria-label="Última página"
          >
            <ChevronsRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}
    </nav>
  );
}
